import R from 'ramda';
import dataFindSelection from '../findSelection';
import { createSelector } from 'reselect';
import {
    selectContent,
    getWordData,
    selectPage,
    selectShowOthersHighlights,
} from './content';
import { selectSection } from './section';
import { selectUser, isInstructorInSection } from './current_user';
import deprecated from '../deprecated.js';

import { param } from './utils';

const selectMarkupStoreRoot = store => store.markup;

// This is a NOOP, but it's here to be more explicit about the fact that we
// don't want selectMarkupStoreRoot
const selectRoot = root => root;

export const selectMarkupTree = createSelector(
    selectMarkupStoreRoot,
    state => state.markup
);

export const selectAllMarkup = createSelector(
    selectMarkupTree,
    tree => R.values(tree)
);

function sortMarkup(a, b) {
    // # page number first
    const pageA = parseInt(a.page, 10);
    const pageB = parseInt(b.page, 10);

    const timeA = Date.parse(a.created);
    const timeB = Date.parse(b.created);

    // First check based on the page index, if they're both the same, it will
    // return in the order they were created.
    return pageA - pageB || timeB - timeA;
}

// (state, content) => [ ...markup ]
export const getByContent = createSelector(
    selectAllMarkup,
    param(1),
    (allMarkup, content) => {
        if (content == null) {
            throw new Error('Missing content');
        }

        const tmp = R.compose(
            R.sort(sortMarkup),
            R.filter(R.propEq('content_id', content.id))
        );

        return tmp(allMarkup);
    }
);

export const selectMarkupByPage = createSelector(
    (root, content) => getByContent(root, content),
    (root, content, pagenum) => pagenum,
    (markup, pagenum) => {
        /* eslint-disable eqeqeq */
        return markup.filter(m => m.page == pagenum);
        /* eslint-enable eqeqeq */
    }
);

// ([ ...markup ]) => [ ...markup ]
const hasSelections = R.compose(
    R.lt(0),
    R.pathOr(0, ['selections', 'length'])
);

// (state, content) => [ ...highlights ]
export const highlightsByContent = R.compose(
    R.filter(hasSelections),
    getByContent
);

// (state, content, pagenum) => [ ...highlights ]
export const highlightsOnPage = (state, content, pagenum) => {
    const samePage = R.filter(R.propEq('page', pagenum));

    return samePage(highlightsByContent(state, content));
};

const isInstructorForContent = (state, content) => {
    const section = selectSection(state, content.section_id);
    return isInstructorInSection(state, section);
};

// (state, content, pagenum) => [ ...highlights ]
export const selectVisibleHighlights = createSelector(
    param(0),
    highlightsOnPage,
    (state, content) => isInstructorForContent(state, content),
    (state, content) => selectShowOthersHighlights(state, content),
    (state, highlights, isInstructor, showOthersHighlights) => {
        return highlights.filter(highlight => {
            if (isMine(state, highlight)) {
                return true;
            }
            if (isInstructor) {
                return (
                    isInstructors(state, highlight) ||
                    (showOthersHighlights && highlight.is_question)
                );
            }
            return showOthersHighlights;
        });
    }
);

// (state, question) => markup
export const getAnswerFor = createSelector(
    selectAllMarkup,
    param(1),
    (markup, question) => {
        if (!question.is_question) {
            throw new Error('Not a question #{question.id}');
        }

        return markup.find(m => m.in_response_to === question.resource_uri);
    }
);

// (state) => [ ...questions ]
export const getUnansweredQuestions = createSelector(
    selectRoot,
    selectAllMarkup,
    (root, allMarkup) => {
        const isQuestion = m => {
            if (m.is_question) {
                return Boolean(getAnswerFor(root, m));
            }
            return false;
        };

        return R.filter(isQuestion, allMarkup);
    }
);

// Notes have note text and are not responses
const isNote = m => {
    return (
        m.note_text != null &&
        m.note_text.length > 0 &&
        m.in_response_to == null
    );
};

// (state, content) => [ ...markup ]
export const selectNotesByContent = createSelector(
    state => state,
    getByContent,
    (state, content) => {
        return content.filter(isNote).map(note => {
            return note.is_question
                ? { ...note, answer: getAnswerFor(state, note) }
                : note;
        });
    }
);
export const getNotesByContent = deprecated(
    selectNotesByContent,
    `use selectNotesByContent instead`
);

// (state, cid) => markup
export const selectMarkupByCID = createSelector(
    selectAllMarkup,
    param(1),
    (markups, cid) => {
        /* eslint-disable eqeqeq */
        const tmp = R.compose(
            R.head,
            R.filter(m => m.cid == cid)
        );
        /* eslint-enable eqeqeq */

        return tmp(markups, cid);
    }
);
export const byCID = selectMarkupByCID;

// (state, id|cid) => markup
export const getMarkup = createSelector(
    selectRoot,
    param(1),
    selectMarkupTree,
    (root, id, markupTree) => {
        if (typeof id === 'string') {
            return byCID(root, id);
        }

        return markupTree[id];
    }
);

// (store, markup) => Boolean
export const isInstructors = createSelector(
    param(0),
    param(1),
    (root, markup) => selectContent(root, markup.content_id),
    (root, markup, content) => {
        const section = selectSection(root, content.section_id);

        return R.contains(markup.author, section.instructors);
    }
);

export const isMine = createSelector(
    param(1),
    root => selectUser(root).resource_uri,
    (markup, currentUser) => {
        // If the author is unkown it must have been created locally and not
        // saved yet.
        if (markup.author == null) {
            return true;
        }

        const version = /\/v\d+\//;
        // # Normalize URLs
        return (
            markup.author.replace(version, '') ===
            currentUser.replace(version, '')
        );
    }
);

export const countUnansweredQuestions = createSelector(
    selectMarkupStoreRoot,
    param(1),
    (state, content) => {
        return R.propOr(0, content.id, state.unansweredCounts);
    }
);

// (state, content) => number
export const selectNoteCount = createSelector(
    selectMarkupStoreRoot,
    param(1),
    (state, content) => {
        return R.pathOr(0, ['noteCounts', content.id], state);
    }
);
export const countNotes = selectNoteCount;

// (state, content) => boolean
export const hasNoteCount = createSelector(
    selectMarkupStoreRoot,
    param(1),
    (state, content) => {
        return Boolean(R.pathOr(false, ['noteCounts', content.id], state));
    }
);

// (state, content, page, highlightBoundingBox) => [ ? ]
export const findSelection = createSelector(
    param(3),
    (root, content, page) => getWordData(root, content, page),
    (highlightBoundingBox, wordData) => {
        return dataFindSelection(wordData, highlightBoundingBox);
    }
);

// (state, notes) => [ ...pages ]
export const selectPagesFromNotes = createSelector(
    param(0),
    param(1),
    (state, notes) => {
        return notes.reduce((pages, note) => {
            const content = selectContent(state, note.content_id);
            const pagenum = note.page;
            if (!pages[pagenum]) {
                pages[pagenum] = selectPage(state, content, pagenum).label;
            }
            return pages;
        }, {});
    }
);

// (state, notes) => [ ...notes ]
export const selectUnansweredFromNotes = createSelector(
    param(0),
    param(1),
    (state, notes) =>
        notes.filter(n => {
            return n.is_question && !getAnswerFor(state, n);
        })
);



// WEBPACK FOOTER //
// ./src/selectors/markup.js